/**
 * SSE Service - Singleton service for managing Server-Sent Events connection
 *
 * Features:
 * - Automatic reconnection with exponential backoff
 * - Event parsing and type-safe callbacks
 * - Connection state management
 * - Graceful cleanup
 * - Cross-tab connection sharing (one EventSource for all tabs of an origin)
 *
 * Cross-tab sharing (PLT-9510)
 * ----------------------------
 * Browsers cap an origin at 6 concurrent HTTP/1.1 connections. Each tab that
 * opened its own long-lived SSE stream permanently consumed one of those slots,
 * so ~6 tabs starved every regular `/api/*` request until it timed out.
 *
 * To fix that, only ONE tab (the "leader") holds the real EventSource. It is
 * elected via the Web Locks API (`navigator.locks`), whose lock is released
 * automatically when the leader tab closes or crashes, so failover needs no
 * heartbeat bookkeeping. The leader relays every SSE event to the other tabs
 * over a `BroadcastChannel`; "follower" tabs open no server connection and feed
 * the relayed events into the exact same callbacks a direct connection would.
 *
 * When either primitive is unavailable (old browsers, jsdom in tests) the
 * service transparently falls back to the legacy behaviour: every tab opens its
 * own EventSource. That path is exercised by the existing unit tests.
 */
import { API_CONFIG } from '@/config/api';
import type {
  SSEConnectedEvent,
  SSEConnectionState,
  SSEHeartbeatEvent,
  SSEServiceCallbacks,
  SSEStatusEvent,
} from './types';

/** BroadcastChannel name used to fan out SSE events to follower tabs. */
const SSE_CHANNEL_NAME = 'compute-ui-sse';

/** Web Lock name whose holder is the single tab that owns the EventSource. */
const SSE_LEADER_LOCK = 'compute-ui-sse-leader';

/**
 * Minimal structural type for the Web Locks API so we don't depend on the DOM
 * lib shipping `LockManager` (older TS lib targets omit it).
 */
interface WebLockManager {
  request(
    name: string,
    options: { signal?: AbortSignal },
    callback: () => Promise<void>
  ): Promise<void>;
}

/**
 * Messages relayed from the leader tab to follower tabs (and control messages
 * followers send back to the leader). Kept internal; exported only for tests.
 */
export type SSEBroadcast =
  | { kind: 'state'; state: SSEConnectionState }
  | { kind: 'connected'; data: SSEConnectedEvent }
  | { kind: 'status'; data: SSEStatusEvent }
  | { kind: 'heartbeat'; data: SSEHeartbeatEvent }
  | { kind: 'error'; message: string }
  /** A newly-opened follower asking the leader to re-send the current snapshot. */
  | { kind: 'hello' }
  /** A follower asking the leader to retry after an error (manual "reconnect"). */
  | { kind: 'retry' };

/**
 * SSE Service class - manages EventSource connection lifecycle
 */
class SSEService {
  private eventSource: EventSource | null = null;
  private callbacks: SSEServiceCallbacks | null = null;
  private reconnectAttempts = 0;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly maxReconnectAttempts = 10;
  private readonly baseReconnectDelay = 1000; // 1 second
  private readonly maxReconnectDelay = 30000; // 30 seconds

  // --- Cross-tab sharing state (only used when sharing is supported) ---
  /** Whether connect() has already set up the shared-mode machinery. */
  private started = false;
  /** True on the single tab that owns the real EventSource. */
  private isLeader = false;
  /** Channel used to relay events between tabs; null in legacy mode. */
  private channel: BroadcastChannel | null = null;
  /** Aborts a pending (queued) leadership request when we disconnect. */
  private lockAbort: AbortController | null = null;
  /** Resolves the lock-holding promise to relinquish leadership. */
  private releaseLeadership: (() => void) | null = null;
  /** Last state/connected event the leader emitted, replayed for new followers. */
  private lastBroadcastState: SSEConnectionState | null = null;
  private lastConnected: SSEConnectedEvent | null = null;

  /**
   * Initialize the service with callbacks
   * Must be called before connect()
   */
  init(callbacks: SSEServiceCallbacks): void {
    this.callbacks = callbacks;
  }

  /**
   * Get the SSE endpoint URL
   */
  private getEndpointUrl(): string {
    const baseUrl = API_CONFIG.baseUrl;
    // Remove trailing slash if present
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    return `${cleanBaseUrl}${API_CONFIG.sseEndpoint}`;
  }

  /**
   * Whether this environment supports cross-tab connection sharing. Requires
   * BOTH BroadcastChannel (event fan-out) and the Web Locks API (leader
   * election). When false we fall back to a direct per-tab connection.
   */
  private supportsSharing(): boolean {
    return (
      typeof BroadcastChannel !== 'undefined' &&
      typeof navigator !== 'undefined' &&
      !!(navigator as unknown as { locks?: WebLockManager }).locks
    );
  }

  private getLockManager(): WebLockManager {
    return (navigator as unknown as { locks: WebLockManager }).locks;
  }

  /**
   * Connect to the SSE endpoint.
   *
   * In shared mode this sets up the BroadcastChannel + leader election on first
   * call; subsequent calls are treated as a manual "reconnect" (retry now if
   * we're the leader, or ask the leader to retry if we're a follower). In
   * legacy mode it opens a direct EventSource, exactly as before.
   */
  connect(): void {
    if (!this.supportsSharing()) {
      this.openDirectConnection();
      return;
    }

    if (!this.started) {
      this.started = true;
      this.channel = new BroadcastChannel(SSE_CHANNEL_NAME);
      this.channel.onmessage = (ev: MessageEvent<SSEBroadcast>) => {
        this.handleChannelMessage(ev.data);
      };
      // Show "connecting" locally while we wait for leadership, and ask any
      // existing leader to replay its current snapshot so we don't sit blank.
      this.setState('connecting');
      this.postToChannel({ kind: 'hello' });
      this.requestLeadership();
      return;
    }

    // Already started — treat as a manual reconnect request.
    if (this.isLeader) {
      this.reconnectAttempts = 0;
      this.openDirectConnection();
    } else {
      this.postToChannel({ kind: 'retry' });
    }
  }

  /**
   * Request the leadership lock. The lock-holding callback returns a promise
   * that stays pending until we relinquish leadership (disconnect) or the tab
   * is closed — at which point the browser auto-releases the lock and the next
   * queued tab becomes leader.
   */
  private requestLeadership(): void {
    const controller = new AbortController();
    this.lockAbort = controller;

    this.getLockManager()
      .request(SSE_LEADER_LOCK, { signal: controller.signal }, () => {
        return new Promise<void>((resolve) => {
          this.releaseLeadership = resolve;
          this.becomeLeader();
        });
      })
      .catch((err: unknown) => {
        // AbortError: we disconnected before acquiring the lock — expected.
        if ((err as { name?: string })?.name === 'AbortError') return;
        // Web Locks failed unexpectedly — degrade to a direct per-tab
        // connection so this tab still receives updates.
        this.isLeader = true;
        this.openDirectConnection();
      });
  }

  /**
   * Become the leader: open the single real EventSource for this origin.
   */
  private becomeLeader(): void {
    this.isLeader = true;
    this.openDirectConnection();
  }

  /**
   * Open the real EventSource connection (leader tab, or legacy per-tab mode).
   */
  private openDirectConnection(): void {
    // Don't reconnect if already connected or connecting
    if (
      this.eventSource?.readyState === EventSource.OPEN ||
      this.eventSource?.readyState === EventSource.CONNECTING
    ) {
      return;
    }

    // Close any existing connection
    this.cleanup();

    this.setState('connecting');

    try {
      const url = this.getEndpointUrl();
      // withCredentials sends cookies for Pomerium authentication
      this.eventSource = new EventSource(url, {
        withCredentials: true,
      });

      this.setupEventListeners();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create EventSource';
      this.handleError(message);
    }
  }

  /**
   * Set up event listeners on the EventSource
   */
  private setupEventListeners(): void {
    if (!this.eventSource) return;

    // Handle connection opened (not yet authenticated - wait for 'connected' event)
    this.eventSource.onopen = () => {
      // Reset reconnect attempts on successful open
      this.reconnectAttempts = 0;
    };

    // Handle 'connected' event from server
    this.eventSource.addEventListener('connected', (event: MessageEvent) => {
      try {
        const data: SSEConnectedEvent = JSON.parse(event.data);
        this.deliverConnected(data);
        this.setState('connected');
      } catch (e) {
        console.error('[SSE] Failed to parse connected event:', e);
      }
    });

    // Handle 'status' events (resource changes)
    this.eventSource.addEventListener('status', (event: MessageEvent) => {
      try {
        const data: SSEStatusEvent = JSON.parse(event.data);
        this.deliverStatus(data);
      } catch (e) {
        console.error('[SSE] Failed to parse status event:', e);
      }
    });

    // Handle 'heartbeat' events (keep-alive)
    this.eventSource.addEventListener('heartbeat', (event: MessageEvent) => {
      try {
        const data: SSEHeartbeatEvent = JSON.parse(event.data);
        this.deliverHeartbeat(data);
      } catch (e) {
        console.error('[SSE] Failed to parse heartbeat event:', e);
      }
    });

    // Handle errors (including connection loss)
    this.eventSource.onerror = () => {
      this.handleDisconnect();
    };
  }

  /**
   * Handle disconnection and schedule reconnection
   */
  private handleDisconnect(): void {
    this.cleanup();
    this.setState('reconnecting');
    this.scheduleReconnect();
  }

  /**
   * Schedule a reconnection with exponential backoff
   */
  private scheduleReconnect(): void {
    // Calculate delay with exponential backoff
    const delay = Math.min(
      this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts),
      this.maxReconnectDelay
    );

    // Cap the exponent, not the number of attempts. A long outage must not
    // permanently stop the live feed, and the delay stays at 30 seconds.
    this.reconnectAttempts = Math.min(this.reconnectAttempts + 1, this.maxReconnectAttempts);

    this.reconnectTimeout = setTimeout(() => {
      this.openDirectConnection();
    }, delay);
  }

  /**
   * Handle errors
   */
  private handleError(message: string): void {
    this.deliverError(message);
    this.setState('error');
  }

  /**
   * Disconnect from SSE
   */
  disconnect(): void {
    // Relinquish leadership (resolve the lock-holding promise) and cancel any
    // still-queued leadership request so another tab can take over.
    if (this.releaseLeadership) {
      this.releaseLeadership();
      this.releaseLeadership = null;
    }
    if (this.lockAbort) {
      this.lockAbort.abort();
      this.lockAbort = null;
    }

    this.cleanup();

    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }

    this.isLeader = false;
    this.started = false;
    this.lastBroadcastState = null;
    this.lastConnected = null;

    // Channel is closed and we are no longer leader, so this is local-only.
    this.setState('disconnected');
  }

  /**
   * Clean up resources (close connection, clear timeouts)
   */
  private cleanup(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }

  /**
   * Reset reconnection attempts (for manual retry)
   */
  resetReconnectAttempts(): void {
    this.reconnectAttempts = 0;
  }

  /**
   * Check if currently connected
   */
  isConnected(): boolean {
    return this.eventSource?.readyState === EventSource.OPEN;
  }

  /**
   * Get current connection state
   */
  getConnectionState(): SSEConnectionState {
    if (!this.eventSource) {
      return 'disconnected';
    }

    switch (this.eventSource.readyState) {
      case EventSource.CONNECTING:
        return 'connecting';
      case EventSource.OPEN:
        return 'connected';
      case EventSource.CLOSED:
        return 'disconnected';
      default:
        return 'disconnected';
    }
  }

  // --- Event delivery: notify local callbacks and (if leader) fan out ---

  /**
   * Emit a connection-state change locally, and relay it to follower tabs when
   * we own the connection. In legacy mode `isLeader` is false and `channel` is
   * null, so this collapses to a plain callback invocation.
   */
  private setState(state: SSEConnectionState): void {
    this.callbacks?.onStateChange(state);
    if (this.isLeader && this.channel) {
      this.lastBroadcastState = state;
      this.postToChannel({ kind: 'state', state });
    }
  }

  private deliverConnected(data: SSEConnectedEvent): void {
    this.callbacks?.onConnected(data);
    if (this.isLeader && this.channel) {
      this.lastConnected = data;
      this.postToChannel({ kind: 'connected', data });
    }
  }

  private deliverStatus(data: SSEStatusEvent): void {
    this.callbacks?.onStatusEvent(data);
    if (this.isLeader && this.channel) {
      this.postToChannel({ kind: 'status', data });
    }
  }

  private deliverHeartbeat(data: SSEHeartbeatEvent): void {
    this.callbacks?.onHeartbeat(data);
    if (this.isLeader && this.channel) {
      this.postToChannel({ kind: 'heartbeat', data });
    }
  }

  private deliverError(message: string): void {
    this.callbacks?.onError(message);
    if (this.isLeader && this.channel) {
      this.postToChannel({ kind: 'error', message });
    }
  }

  private postToChannel(msg: SSEBroadcast): void {
    // BroadcastChannel.postMessage can throw if the channel was closed mid-flight.
    try {
      this.channel?.postMessage(msg);
    } catch {
      // Channel gone — nothing to relay to.
    }
  }

  /**
   * Handle a message from another tab over the BroadcastChannel.
   *
   * Leader: answers control messages ('hello' → replay snapshot, 'retry' →
   * reconnect). Follower: applies relayed SSE events to the local callbacks so
   * this tab's UI stays live without opening its own connection.
   */
  private handleChannelMessage(msg: SSEBroadcast): void {
    if (!msg) return;

    if (this.isLeader) {
      if (msg.kind === 'hello') {
        // A follower just joined — replay the latest snapshot so it renders
        // the current state immediately instead of waiting for the next event.
        if (this.lastBroadcastState) {
          this.postToChannel({ kind: 'state', state: this.lastBroadcastState });
        }
        if (this.lastConnected) {
          this.postToChannel({ kind: 'connected', data: this.lastConnected });
        }
      } else if (msg.kind === 'retry') {
        this.reconnectAttempts = 0;
        this.openDirectConnection();
      }
      return;
    }

    // Follower: mirror the leader's events onto our local callbacks.
    switch (msg.kind) {
      case 'state':
        this.callbacks?.onStateChange(msg.state);
        break;
      case 'connected':
        this.callbacks?.onConnected(msg.data);
        break;
      case 'status':
        this.callbacks?.onStatusEvent(msg.data);
        break;
      case 'heartbeat':
        this.callbacks?.onHeartbeat(msg.data);
        break;
      case 'error':
        this.callbacks?.onError(msg.message);
        break;
      case 'hello':
      case 'retry':
        // Control messages only the leader acts on.
        break;
    }
  }
}

// Export singleton instance
export const sseService = new SSEService();
